import { BidAlerts } from '@components/bid-buy/BidAlerts';
import BidBuyHeadline from '@components/bid-buy/BidBuyHeadline';
import { BidBuySubmit } from '@components/bid-buy/BidBuySubmit';
import BidBuyTimer from '@components/bid-buy/BidBuyTimer';
import BuyingTimeRadios from '@components/bid-buy/BuyingTimeRadios';
import { FirstCallOverrideSelect } from '@components/bid-buy/FirstCallOverrideSelect';
import { KVPSSelect } from '@components/bid-buy/KVPSSelect';
import { TransportSelect } from '@components/bid-buy/TransportSelect';
import DeliveryAddressSelect from '@components/dealer-table/DeliveryAddressSelect';
import { Grid, Paper } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Control, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useRole from 'src/hooks/useRole';
import { useGetTransportOptions } from 'src/hooks/useTransportOptions';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import UserRole from 'src/types/UserRoles';

import BidForm from '../components/bid-buy/BidForm';
import useBidBuyStyles from '../components/bid-buy/useBidBuyStyles';
import CarDetailHeadline from '../components/CarDetailHeadline';
import DeleteBidDialog from '../components/DeleteBidDialog';
import { noKVPSSources, useBidBuyForm } from '../hooks/useBidBuyForm';
import {
  useAccountDealerId,
  getBidBuyErrors,
  getBiddingStatus,
  isFixedBidRetryable,
  selectDefaultTransportOption,
} from '../modules/bid-buy-helpers';
import {
  BidDTO,
  BidSuccessStatus,
  DetailCarDTO,
  KvpsType,
  MarketingChannel,
  SourceType,
} from '../modules/generated/api';
import { setToArray } from '../modules/util';

type BidBuyProps = {
  car: DetailCarDTO;
  refresh: () => Promise<void>;
};

const BidBuy = ({ car, refresh }: BidBuyProps) => {
  const classes = useBidBuyStyles();
  const { t } = useTranslation();
  const { hasRole } = useRole();
  const allowedToSetBuyingTime = useUserPermissions(UserPermissions.FIXED_BUY_TIME);

  const {
    form: { getValues, control, watch, setValue, clearErrors },
    form,
    resetAndRefetch,
    fetchCalculation,
  } = useBidBuyForm(car);
  const [bidForDeletion, setBidForDeletion] = useState<BidDTO | undefined>();
  const currentDealerIds = useAccountDealerId();
  const actualBidSuccessState = getBiddingStatus(car.bid);
  const [bidEditMode, setBidEditMode] = useState(true);
  const marketingChannel = car.marketingChannel || [];
  const supportsBidding = marketingChannel.includes(MarketingChannel.Bid);
  const supportsBuying = marketingChannel.includes(MarketingChannel.Fixed);
  const supportsBidAutomation = supportsBidding && car.source === SourceType.Vwfs && !car.packageId;
  const isNoKVPSCar = noKVPSSources.includes(car.source!); // Especially for UCS -> No KVPS needed

  // First call override, if other dealer than self is selected then kvps & delivery address are hidden to avoid errors
  const overrideFirstCallActive = watch('overrideFirstCallCascade') === 'yes';
  const overrideFirstCallDealer = watch('overridingFirstCallCascadeDealer');
  const overrideFirstCallDealerOther =
    overrideFirstCallDealer !== undefined && !currentDealerIds?.includes(overrideFirstCallDealer);

  const adminCanOverride = !!car.bidStateInfo?.adminImmediatePurchasePossible;
  const fixedBidRetryable = isFixedBidRetryable(car.bid, car.dateEnd);
  const errors = getBidBuyErrors({ isNoKVPSCar, kvps: car.kvps });
  const hasErrors = !isEmpty(errors);
  const allowedDeliveryAddressIds = car.deliveryAddressSelection?.selectedAddressByKVPS?.[getValues('kvps')] || [];
  const toggleEdit = () => {
    if (bidEditMode) resetAndRefetch(); // When leaving edit mode without submitting formState must be reset
    setBidEditMode((prevState) => !prevState);
  };
  const onClose = () => setBidForDeletion(undefined);

  useEffect(() => {
    setBidEditMode(
      adminCanOverride ||
        (!(
          actualBidSuccessState === BidSuccessStatus.Bought ||
          actualBidSuccessState === BidSuccessStatus.Sent ||
          actualBidSuccessState === BidSuccessStatus.Scheduled
        ) &&
          !hasErrors),
    );
  }, [actualBidSuccessState, hasErrors, car.bid?.bids, adminCanOverride]);

  useEffect(() => {
    // Reset errors when first call override set to another dealer than self
    if (overrideFirstCallDealerOther) {
      clearErrors('deliveryAddress');
      clearErrors('kvps');
      clearErrors('selectedTransportOption');
    }
  }, [clearErrors, overrideFirstCallDealerOther]);

  const isFirstCallCar =
    car.virtualSources &&
    (car.virtualSources.includes(SourceType.RealFirstCallOwn) ||
      car.virtualSources.includes(SourceType.RealFirstCallOther));

  const deliveryId = watch('deliveryAddress');
  const kvps = watch('kvps');
  const selectedKvpsValue = getValues('kvps');
  // with a Central KVPS the Transportation-Select is not displayed
  const noCentralKvpsSelected = car.kvps
    ? car.kvps.find((k) => k.kvps === selectedKvpsValue)?.type !== KvpsType.Central
    : true;
  const selectedTransportOption = watch('selectedTransportOption');
  const dataIsMissing = !car.source || !car.carId || !deliveryId || !kvps;
  const { data: transportOptions, isLoading: transportOptionsLoading } = useGetTransportOptions(
    car.source || SourceType.Vwfs,
    car.carId || '',
    deliveryId || '',
    kvps || '',
  );

  // make sure the selected transport option is still available
  useEffect(() => {
    if (
      selectedTransportOption &&
      transportOptions?.options &&
      !transportOptions.options.map((option) => option.id).includes(selectedTransportOption)
    ) {
      setValue('selectedTransportOption', '', { shouldValidate: true });
    }
  }, [
    bidEditMode,
    selectedTransportOption,
    car?.bid?.selectedTransportOption,
    car?.bid?.selectedTransportOption?.id,
    deliveryId,
    getValues,
    kvps,
    setValue,
    transportOptions?.options,
  ]);

  // set a default transport option for a better user experience if possible and necessary
  useEffect(() => {
    if (
      car.source === SourceType.Vwfs &&
      !overrideFirstCallDealerOther &&
      getValues('selectedTransportOption') === '' &&
      transportOptions?.options
    ) {
      const isOwnFirstCall = car.virtualSources?.includes(SourceType.RealFirstCallOwn);
      setValue('selectedTransportOption', selectDefaultTransportOption(transportOptions.options, isOwnFirstCall), {
        shouldValidate: true,
      });
    }
  }, [
    car.source,
    clearErrors,
    getValues,
    overrideFirstCallDealerOther,
    setValue,
    transportOptions,
    kvps,
    car.virtualSources,
  ]);

  return (
    <FormProvider {...form}>
      <Paper elevation={3} className={classes.bidBuy}>
        <div className={classes.headlineWrapper}>
          <BidBuyHeadline
            bidEditMode={bidEditMode}
            actualBidSuccessState={actualBidSuccessState}
            isFixedBidRetryable={fixedBidRetryable}
            adminCanOverride={adminCanOverride}
            toggleEdit={toggleEdit}
            onDelete={() => {
              const sameDealerOtherBids = (car.bidStateInfo?.otherCurrentBids ?? []).filter((bid) =>
                currentDealerIds?.includes(bid.dealer?.id ?? ''),
              );
              setBidForDeletion({
                ...car.bid!,
                bids: [...(car.bid?.bids ?? []), ...sameDealerOtherBids],
              });
            }}
          />
          <div className={classes.header}>
            <CarDetailHeadline
              title={`${
                (supportsBidding && actualBidSuccessState !== BidSuccessStatus.Bought && t('common.toBid')) || ''
              }${
                (supportsBidding && supportsBuying && actualBidSuccessState !== BidSuccessStatus.Bought && ' & ') || ''
              }${(supportsBuying && t('common.toBuy')) || ''}`}
            />
            <div>
              <Grid container direction="column">
                {!isNoKVPSCar && !overrideFirstCallDealerOther && (
                  <Grid item>
                    <KVPSSelect
                      rules={{ required: !isNoKVPSCar }}
                      kvps={car.kvps}
                      disabled={!bidEditMode || isNoKVPSCar || overrideFirstCallDealerOther}
                      className={classes.select}
                    />
                  </Grid>
                )}
                {!overrideFirstCallDealerOther && (
                  <Grid item>
                    <DeliveryAddressSelect
                      name="deliveryAddress"
                      rules={{ required: true }}
                      control={control}
                      deliveryAddresses={setToArray(car.deliveryAddressSelection?.deliveryAddresses).filter(
                        (address) => (isNoKVPSCar ? true : allowedDeliveryAddressIds.includes(address.id as string)),
                      )}
                      disabled={!bidEditMode || overrideFirstCallDealerOther}
                      className={classes.select}
                      setValue={setValue}
                    />
                  </Grid>
                )}
                {car.source === SourceType.Vwfs && noCentralKvpsSelected && !overrideFirstCallDealerOther && (
                  <TransportSelect
                    source={car.source}
                    options={transportOptions?.options}
                    loading={transportOptionsLoading}
                    dataIsMissing={dataIsMissing}
                    disabled={!bidEditMode || overrideFirstCallDealerOther}
                    className={classes.select}
                  />
                )}
                {overrideFirstCallActive && (
                  <Grid item>
                    <FirstCallOverrideSelect bids={car.bidStateInfo?.otherCurrentBids} className={classes.select} />
                  </Grid>
                )}
              </Grid>
            </div>
          </div>
          <div className={classes.printHide}>
            <BidBuyTimer car={car} refresh={refresh} />
          </div>
        </div>

        <BidAlerts
          car={car}
          errors={errors}
          className={classes.bidAlerts}
          supportsBidding={supportsBidding}
          supportsBuying={supportsBuying}
        />

        {supportsBidding &&
          car.source &&
          car.carId &&
          // getBiddingStatus returns Bought for all fixed bids; adminCanOverride bypasses this for scheduled spotdeals
          (actualBidSuccessState !== BidSuccessStatus.Bought || adminCanOverride) && (
            <BidForm car={car} bidEditMode={bidEditMode} fetchCalculation={fetchCalculation} />
          )}

        {supportsBuying && ((isFirstCallCar && hasRole(UserRole.ADMIN)) || allowedToSetBuyingTime) && bidEditMode && (
          <div style={{ width: '100%', marginTop: 12, marginBottom: 12 }}>
            <BuyingTimeRadios
              name="overrideFirstCallCascade"
              control={control as unknown as Control}
              activateForm={bidEditMode}
              isFirstCall={isFirstCallCar}
              endDate={car.dateEnd}
            />
          </div>
        )}

        <BidBuySubmit
          car={car}
          supportsBuying={supportsBuying}
          supportsBidding={supportsBidding}
          supportsBidAutomation={supportsBidAutomation}
          buyPrice={car.potential?.base?.amount}
          actualBidSuccessState={actualBidSuccessState}
          refresh={refresh}
          bidEditMode={bidEditMode}
          errors={errors}
          transportOptionsLoading={transportOptionsLoading}
        />
      </Paper>
      {bidForDeletion && <DeleteBidDialog onClose={onClose} bidForDeletion={bidForDeletion} reload={refresh} />}
    </FormProvider>
  );
};
export default BidBuy;
